import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	type ExternalUserType,
	type InvitedGroup,
	type InvitedUser,
	isUserWithEmail,
} from '../../types';
import { type User, type OptionData, type ExternalUser } from '@atlaskit/user-picker';
import { type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { isEmail, isUser, isExternalUser } from '@atlaskit/user-picker';
import { InviteeListAnalytics } from './InviteeListAnalytics';
import { useAnalytics } from '../../analytics';

export type OptionType = OptionData | User | ExternalUser;

export type InviteeListState = {
	allowEmail: boolean;
	// userPickerOptions is used for userPicker state.
	userPickerOptions: OptionType[];
	setUserPickerOptions: React.Dispatch<React.SetStateAction<OptionType[]>>;
	// derived from userPickerOptions
	emails: string[];
	// derived from userPickerOptions
	ids: string[];
	// derived from userPickerOptions
	users: InvitedUser[];
	groups: InvitedGroup[];
	selectedTeam?: OptionData;
	setSelectedTeam: React.Dispatch<React.SetStateAction<OptionData | undefined>>;
	trailingText: string;
	setTrailingText: React.Dispatch<React.SetStateAction<string>>;
	getName: (id: string) => string;
	getUser: (id: string) => OptionType | undefined;
	getExternalUserType: (user: InvitedUser) => ExternalUserType | undefined;
	selectedSlackTeamUserEmails: Set<string>;
	selectUsersFromSlackTeam: (
		selectedTeamUsers: ExternalUser[],
		allTeamUsers: ExternalUser[],
	) => void;
	experimental_userPickerByline?: string;
};

export const InviteeListStateContext = React.createContext<InviteeListState | undefined>(undefined);

export interface InviteeListContextProps {
	allowEmail?: boolean;
	children: React.ReactElement;
	initialInvitees?: OptionType[];
	experimental_userPickerByline?: string;
}

export const getEmailsAndIds = (
	options: OptionType[],
	includeGroupsInIds: boolean,
): InvitedUser[] =>
	options
		.filter((option) => includeGroupsInIds || option.type !== 'group')
		.map((option) => {
			if (isEmail(option)) {
				const nameOrId = option.name || option.id;
				return { email: nameOrId };
			}
			if (isUser(option)) {
				if (option.email) {
					return { email: option.email };
				} else {
					const id = option.id;
					return { id };
				}
			}
			// Use the id if option is not of type USER or EMAIL
			const id = option.id;
			return { id };
		});

export const getGroupIds = (options: OptionType[]): InvitedGroup[] =>
	options.filter((option) => option.type === 'group').map(({ id }) => ({ id }));

export const getEmailAndIdArrays = (options: OptionType[]): { emails: string[]; ids: string[] } => {
	const emailsAndIds = getEmailsAndIds(options, true);
	return {
		emails: emailsAndIds.flatMap((item) =>
			isUserWithEmail(item) && item.email ? [item.email] : [],
		),
		// only get ID if email does not exist and ID exists.
		ids: emailsAndIds.flatMap((item) => (item.id && !isUserWithEmail(item) ? [item.id] : [])),
	};
};

export const getExternalUserTypeFromOptions = (options: OptionType[], invitedUser: InvitedUser) => {
	const user = options.find((option) => {
		if (isExternalUser(option)) {
			return option.email === invitedUser.email || option.id === invitedUser.id;
		}
		return false;
	});
	return user ? (user as ExternalUser).externalUserType : undefined;
};

export const InviteeListContext = ({
	allowEmail,
	children,
	initialInvitees,
	experimental_userPickerByline,
}: InviteeListContextProps & WithAnalyticsEventsProps) => {
	const analytics = useAnalytics(InviteeListAnalytics);
	const [userPickerOptions, setUserPickerOptions] = useState<OptionType[]>(
		() => initialInvitees ?? [],
	);
	const [trailingText, setTrailingText] = React.useState('');
	const [selectedTeam, setSelectedTeam] = useState<OptionData | undefined>();

	const [selectedSlackTeamUserEmails, setSelectedSlackTeamUserEmails] = useState<Set<string>>(
		new Set(),
	);

	useEffect(() => {
		setSelectedSlackTeamUserEmails(
			(selectedSlackTeamUserEmails) =>
				new Set(
					userPickerOptions
						.filter((user) => selectedSlackTeamUserEmails.has((user as ExternalUser).email!))
						.map((user) => (user as ExternalUser).email!),
				),
		);
	}, [userPickerOptions]);

	useEffect(() => {
		if (initialInvitees && initialInvitees.length > 0) {
			analytics.inviteeListPrefilled(initialInvitees.length);
		}
	}, [initialInvitees, analytics]);

	// emails ands ids that are in the user picker. This array will be used when inviting.
	const users = getEmailsAndIds(userPickerOptions, false);
	const groups = getGroupIds(userPickerOptions);
	// emails and ids as string arrays.
	const { emails, ids } = getEmailAndIdArrays(userPickerOptions);

	const contextValue = useMemo<InviteeListState>(() => {
		const selectUsersFromSlackTeam = (
			selectedTeamUsers: ExternalUser[],
			allTeamUsers: ExternalUser[],
		) => {
			const selectedTeamEmails = selectedTeamUsers.map((user) => user.email!);
			const selected = new Set(selectedTeamEmails);
			const unselected = new Set(
				allTeamUsers.filter((user) => !selected.has(user.email!)).map((user) => user.email),
			);
			setSelectedSlackTeamUserEmails(
				new Set([
					...[...selectedSlackTeamUserEmails].filter((email) => !unselected.has(email)),
					...selectedTeamEmails,
				]),
			);
			const existingEmails = new Set(userPickerOptions.map((user) => (user as ExternalUser).email));
			setUserPickerOptions([
				...userPickerOptions.filter((user) => !unselected.has((user as ExternalUser).email!)),
				...selectedTeamUsers.filter((user) => !existingEmails.has(user.email)),
			]);
		};
		const getName = (id: string) => {
			return userPickerOptions.find((item) => item.id === id)?.name || id;
		};
		const getUser = (id: string) => userPickerOptions.find((user) => user.id === id);
		const getExternalUserType = (user: InvitedUser) =>
			getExternalUserTypeFromOptions(userPickerOptions, user);

		return {
			// NOTE: default value of `allowEmail` is `true` given backwards compatibility,
			//       since UserPicker's `allowEmail` was always `true` before the Invite People `allowEmail` prop
			allowEmail: allowEmail ?? true,
			userPickerOptions,
			setUserPickerOptions,
			selectedTeam,
			setSelectedTeam,
			trailingText,
			setTrailingText,
			users,
			groups,
			emails,
			ids,
			getName,
			getUser,
			getExternalUserType,
			selectedSlackTeamUserEmails,
			selectUsersFromSlackTeam,
			experimental_userPickerByline,
		};
	}, [
		allowEmail,
		emails,
		groups,
		ids,
		selectedSlackTeamUserEmails,
		selectedTeam,
		trailingText,
		userPickerOptions,
		users,
		experimental_userPickerByline,
	]);

	return (
		<InviteeListStateContext.Provider value={contextValue}>
			{children}
		</InviteeListStateContext.Provider>
	);
};

export const useInviteeListStateContext = () => {
	const state = useContext(InviteeListStateContext);

	if (state === undefined) {
		throw new Error(
			'useInviteeListStateContext must be used within a InviteeListStateContextProvider',
		);
	}

	return state;
};
